import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheck, Scissors } from 'lucide-react';
interface Booking {
  id: number;
  name: string;
  location: string;
  service: string;
  price: string;
  thumbnail: string;
}
const NAMES = ['Martín', 'Santiago', 'Tomás', 'Nicolás', 'Facundo', 'Agustín', 'Matías', 'Joaquín', 'Lucas', 'Federico', 'Gonzalo', 'Ramiro', 'Sebastián', 'Ignacio', 'Lautaro', 'Franco', 'Maximiliano', 'Emiliano', 'Valentín', 'Bruno', 'Thiago', 'Bautista', 'Dante', 'Benicio', 'Ciro', 'Leandro', 'Ezequiel', 'Rodrigo', 'Andrés', 'Pablo'];
const LOCATIONS = ['Alberdi', 'Pichincha', 'Abasto', 'Centro', 'Luis Agote', 'Funes', 'Roldán', 'Pérez', 'Arroyito', 'La Florida', 'San Martín', 'Echesortu', 'Ludueña', 'Belgrano', 'Alberto Olmedo'];
const SERVICES = [{
  service: 'Cortes Old School + Arreglo de Barba',
  price: '$25.000'
}, {
  service: 'Afeitado Tradicional',
  price: '$18.000'
}, {
  service: 'Rapado + Afeitado Tradicional',
  price: '$22.000'
}, {
  service: 'Getting Ready — Novio',
  price: 'A convenir'
}, {
  service: 'Afeitado Tradicional + Birra',
  price: '$21.000'
}, {
  service: 'Cortes Old School + Barba + Café',
  price: '$28.000'
}];
const TOAST_DURATION = 4000;
const FIRST_DELAY = 3000;
const MIN_INTERVAL = 8000;
const MAX_INTERVAL = 15000;
function getRandomInterval() {
  return Math.floor(Math.random() * (MAX_INTERVAL - MIN_INTERVAL + 1)) + MIN_INTERVAL;
}
export function RecentBookings() {
  const [current, setCurrent] = useState<Booking | null>(null);
  const recentNames = useRef<string[]>([]);
  const idCounter = useRef(0);
  const prefersReducedMotion = useRef(typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false);
  const pickName = useCallback(() => {
    const available = NAMES.filter(n => !recentNames.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    recentNames.current.push(name);
    if (recentNames.current.length > 10) {
      recentNames.current.shift();
    }
    return name;
  }, []);
  const generateBooking = useCallback((): Booking => {
    const name = pickName();
    const location = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
    const serviceData = SERVICES[Math.floor(Math.random() * SERVICES.length)];
    idCounter.current += 1;
    return {
      id: idCounter.current,
      name,
      location,
      ...serviceData
    };
  }, [pickName]);
  useEffect(() => {
    let hideTimeout: ReturnType<typeof setTimeout>;
    let nextTimeout: ReturnType<typeof setTimeout>;
    const showBooking = () => {
      const booking = generateBooking();
      setCurrent(booking);
      hideTimeout = setTimeout(() => {
        setCurrent(null);
        nextTimeout = setTimeout(showBooking, getRandomInterval());
      }, TOAST_DURATION);
    };
    const initialTimeout = setTimeout(showBooking, FIRST_DELAY);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(hideTimeout);
      clearTimeout(nextTimeout);
    };
  }, [generateBooking]);
  const reduced = prefersReducedMotion.current;
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" data-id="element-203">
      <AnimatePresence mode="wait" data-id="element-204">
        {current && <motion.div key={current.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: reduced ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto" data-id="element-205">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-rotterdam-gold/20 shadow-2xl backdrop-blur-md max-w-sm sm:max-w-md" style={{
          background: 'linear-gradient(135deg, rgba(26,16,8,0.95) 0%, rgba(13,13,13,0.95) 100%)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(107,184,212,0.1)'
        }} data-id="element-206">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg border border-rotterdam-gold/20 flex items-center justify-center bg-rotterdam-gold/10" data-id="element-207">
                <Scissors className="w-6 h-6 text-rotterdam-gold pearl-icon" data-id="element-208" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-209">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-210">
                  <CalendarCheck className="w-3 h-3 text-rotterdam-gold pearl-icon flex-shrink-0" data-id="element-211" />
                  <span className="text-rotterdam-cream/50 text-[10px] tracking-wider uppercase" data-id="element-212">
                    Nuevo turno
                  </span>
                </div>
                <p className="text-rotterdam-cream text-xs leading-snug truncate" data-id="element-213">
                  <span className="font-semibold" data-id="element-214">{current.name}</span>
                  <span className="text-rotterdam-cream/50" data-id="element-215"> en </span>
                  <span className="font-medium" data-id="element-216">{current.location}</span>
                </p>
                <p className="text-rotterdam-cream/70 text-[11px] leading-snug truncate mt-0.5" data-id="element-217">
                  {current.service}
                  {current.price !== 'A convenir' && <span className="text-rotterdam-gold pearl-text font-semibold ml-1" data-id="element-218">
                      — {current.price}
                    </span>}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}